import React, { useCallback, useEffect, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import api from '../../../services/api';
import ItemPicker from './ItemPicker';
import alertService from '../../../utils/alert';
import { cn } from '../../../utils/cn';
import { formatCurrency } from '../../../utils/currency';
import SearchableSelect from '../SearchableSelect';
import { Modal, Btn, INPUT, LABEL, fmtDate, localToday, r2, errMsg, downloadFile } from './ui';

const STATUS = {
  draft: ['Draft', 'bg-[#F3F2F2] text-[#444444]'],
  issued: ['Issued', 'bg-[#E0F2FE] text-[#0176D3]'],
  partially_billed: ['Part billed', 'bg-[#FEF3C7] text-[#92400E]'],
  billed: ['Billed', 'bg-[#DCFCE7] text-[#15803D]'],
  cancelled: ['Cancelled', 'bg-[#F3F4F6] text-[#9CA3AF]'],
};
const blankLine = (accountId = '') => ({ description: '', hsnCode: '', quantity: 1, unitPrice: '', taxRate: 18, expenseAccountId: accountId });

function Pill({ status }) {
  const [label, cls] = STATUS[status] || [status, ''];
  return <span className={cn('px-2 py-0.5 rounded-full text-[11px] font-semibold', cls)}>{label}</span>;
}

function POForm({ po, vendors, accounts, onClose, onSaved }) {
  const def = accounts.find((a) => a.code === '5000')?._id || '';
  const [form, setForm] = useState(() => (po ? {
    vendorId: po.vendorId?._id || po.vendorId, orderDate: po.orderDate?.slice(0, 10), expectedDate: po.expectedDate?.slice(0, 10) || '',
    deliveryAddress: po.deliveryAddress || '', notes: po.notes || '',
    lineItems: po.lineItems.map((l) => ({ ...l, expenseAccountId: l.expenseAccountId || '' })),
  } : { vendorId: '', orderDate: localToday(), expectedDate: '', deliveryAddress: '', notes: '', lineItems: [blankLine(def)] }));
  const [saving, setSaving] = useState(false);
  const setLine = (i, p) => setForm((f) => ({ ...f, lineItems: f.lineItems.map((l, j) => (j === i ? { ...l, ...p } : l)) }));
  const sub = r2(form.lineItems.reduce((s, l) => s + (Number(l.quantity) || 0) * (Number(l.unitPrice) || 0), 0));
  const tax = r2(form.lineItems.reduce((s, l) => s + (Number(l.quantity) || 0) * (Number(l.unitPrice) || 0) * (Number(l.taxRate) || 0) / 100, 0));

  const save = async () => {
    if (!form.vendorId) return alertService.error('Choose a vendor');
    setSaving(true);
    const body = { ...form, lineItems: form.lineItems.map((l) => ({ description: l.description, hsnCode: l.hsnCode, quantity: Number(l.quantity), unitPrice: Number(l.unitPrice) || 0, taxRate: Number(l.taxRate) || 0, expenseAccountId: l.expenseAccountId || undefined, itemId: l.itemId || null })) };
    try {
      if (po) await api.put(`/crm/purchases/purchase-orders/${po._id}`, body);
      else await api.post('/crm/purchases/purchase-orders', body);
      onSaved();
    } catch (e) { alertService.error('Could not save order', errMsg(e)); } finally { setSaving(false); }
  };

  return (
    <Modal title={po ? `Edit ${po.poNumber}` : 'New purchase order'} onClose={onClose} wide
      footer={<><Btn onClick={onClose}>Cancel</Btn><Btn primary disabled={saving} onClick={save}>{saving ? 'Saving…' : 'Save as draft'}</Btn></>}>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="md:col-span-2"><span className={LABEL}>Vendor</span><SearchableSelect value={form.vendorId} onChange={(v) => setForm({ ...form, vendorId: v })} options={vendors} placeholder="Choose vendor…" /></div>
        <div><label className={LABEL} htmlFor="po-date">Order date</label><input id="po-date" type="date" className={INPUT} value={form.orderDate} onChange={(e) => setForm({ ...form, orderDate: e.target.value })} /></div>
        <div><label className={LABEL} htmlFor="po-exp">Expected by</label><input id="po-exp" type="date" className={INPUT} value={form.expectedDate} onChange={(e) => setForm({ ...form, expectedDate: e.target.value })} /></div>
      </div>
      <div className="border border-[#D8DDE6] rounded overflow-x-auto">
        <table className="w-full text-[12px] sm:min-w-[47.5rem]">
          <thead className="text-[#706E6B] text-[11px] uppercase tracking-wide"><tr>
            <th className="text-left font-semibold px-2 py-2">Item</th><th className="text-left font-semibold px-2 py-2 w-48">Expense account</th>
            <th className="text-right font-semibold px-2 py-2 w-20">Qty</th><th className="text-right font-semibold px-2 py-2 w-28">Rate</th>
            <th className="text-right font-semibold px-2 py-2 w-20">Tax %</th><th className="w-8" />
          </tr></thead>
          <tbody>
            {form.lineItems.map((l, i) => (
              <tr key={i} className="border-t border-[#F3F2F2]">
                <td className="px-2 py-1.5">
                  <div className="flex gap-1">
                    <ItemPicker value={l.itemId} className="w-28" onPick={(it) => setLine(i, it
                      ? { itemId: it._id, description: it.description || it.name, hsnCode: it.hsnCode || l.hsnCode, unitPrice: it.purchasePrice || l.unitPrice, taxRate: it.taxRate ?? l.taxRate, ...(it.expenseAccountId ? { expenseAccountId: it.expenseAccountId } : {}) }
                      : { itemId: null })} />
                    <input aria-label="Item description" className={INPUT} value={l.description} onChange={(e) => setLine(i, { description: e.target.value })} />
                  </div>
                </td>
                <td className="px-2 py-1.5">
                  <select aria-label="Expense account" className={INPUT} value={l.expenseAccountId} onChange={(e) => setLine(i, { expenseAccountId: e.target.value })}>
                    <option value="">Vendor default</option>
                    {accounts.map((a) => <option key={a._id} value={a._id}>{a.code} · {a.name}</option>)}
                  </select>
                </td>
                <td className="px-2 py-1.5"><input aria-label="Quantity" type="number" min="0" step="0.01" className={cn(INPUT, 'text-right')} value={l.quantity} onChange={(e) => setLine(i, { quantity: e.target.value })} /></td>
                <td className="px-2 py-1.5"><input aria-label="Rate" type="number" min="0" step="0.01" className={cn(INPUT, 'text-right')} value={l.unitPrice} onChange={(e) => setLine(i, { unitPrice: e.target.value })} /></td>
                <td className="px-2 py-1.5"><input aria-label="Tax percent" type="number" min="0" max="100" step="0.01" className={cn(INPUT, 'text-right')} value={l.taxRate} onChange={(e) => setLine(i, { taxRate: e.target.value })} /></td>
                <td className="px-1">{form.lineItems.length > 1 && <button type="button" aria-label="Remove item" onClick={() => setForm((f) => ({ ...f, lineItems: f.lineItems.filter((_, j) => j !== i) }))} className="p-1 text-[#706E6B]"><Trash2 size={14} /></button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <button type="button" onClick={() => setForm((f) => ({ ...f, lineItems: [...f.lineItems, blankLine(def)] }))} className="text-[12px] font-semibold text-[#0176D3] flex items-center gap-1"><Plus size={14} /> Add item</button>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div><label className={LABEL} htmlFor="po-addr">Deliver to</label><input id="po-addr" className={INPUT} value={form.deliveryAddress} onChange={(e) => setForm({ ...form, deliveryAddress: e.target.value })} /></div>
        <div><label className={LABEL} htmlFor="po-notes">Notes</label><input id="po-notes" className={INPUT} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
      </div>
      <p className="text-[13px] text-right">Order value <strong className="tabular-nums">{formatCurrency(sub + tax)}</strong> <span className="text-[#706E6B]">({formatCurrency(sub)} + {formatCurrency(tax)} tax)</span></p>
    </Modal>
  );
}

function PODetail({ id, onClose, onChanged, onEdit, onOpenBill }) {
  const [data, setData] = useState(null);
  const [qty, setQty] = useState({});
  const [vendorBillNumber, setVbn] = useState('');
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    api.get(`/crm/purchases/purchase-orders/${id}`).then((res) => {
      setData(res.data);
      setQty(Object.fromEntries(res.data.purchaseOrder.lineItems.map((l) => [l._id, String(r2(l.quantity - l.billedQuantity))])));
    }).catch((e) => alertService.error('Could not load order', errMsg(e)));
  }, [id]);
  useEffect(() => { load(); }, [load]);
  if (!data) return <Modal title="Purchase order" onClose={onClose}><p className="text-[13px] text-[#706E6B] text-center py-6">Loading…</p></Modal>;
  const { purchaseOrder: po, bills } = data;
  const c = po.currency;
  const act = async (fn, ok) => { setBusy(true); try { await fn(); alertService.success(ok); load(); onChanged(); } catch (e) { alertService.error('Action failed', errMsg(e)); } finally { setBusy(false); } };
  const canBill = ['issued', 'partially_billed'].includes(po.status);

  return (
    <Modal title={`${po.poNumber} · ${po.vendorId?.name || ''}`} onClose={onClose} wide
      footer={<>
        {po.status === 'draft' && <Btn danger onClick={async () => {
          if (!(await alertService.confirm('Delete draft order?', '', 'danger', 'Delete'))) return;
          try { await api.delete(`/crm/purchases/purchase-orders/${po._id}`); alertService.success('Order deleted'); onChanged(); onClose(); } catch (e) { alertService.error('Could not delete', errMsg(e)); }
        }}>Delete</Btn>}
        {po.status === 'draft' && <Btn onClick={() => onEdit(po)}>Edit</Btn>}
        <Btn onClick={() => downloadFile(`/crm/purchases/purchase-orders/${po._id}/pdf`, `PO-${po.poNumber}.pdf`)}>PDF</Btn>
        {!['billed', 'cancelled'].includes(po.status) && po.status !== 'draft' && <Btn danger onClick={async () => { if (await alertService.confirm('Cancel this order?', 'Bills already raised from it are not affected.', 'danger', 'Cancel order')) act(() => api.post(`/crm/purchases/purchase-orders/${po._id}/cancel`), 'Order cancelled'); }}>Cancel order</Btn>}
        {po.status === 'draft' && <Btn primary disabled={busy} onClick={() => act(() => api.post(`/crm/purchases/purchase-orders/${po._id}/issue`), 'Order issued')}>Issue to vendor</Btn>}
        {canBill && <Btn primary disabled={busy} onClick={() => act(async () => {
          const lines = Object.entries(qty).map(([lineId, q]) => ({ lineId, quantity: Number(q) })).filter((x) => x.quantity > 0);
          const { data: res } = await api.post(`/crm/purchases/purchase-orders/${po._id}/convert`, { lines, vendorBillNumber });
          onOpenBill?.(res.bill._id);
        }, 'Draft bill created')}>Create bill</Btn>}
      </>}>
      <div className="flex flex-wrap items-center justify-between gap-2 text-[13px]">
        <span>Ordered {fmtDate(po.orderDate)}{po.expectedDate && ` · expected ${fmtDate(po.expectedDate)}`}</span>
        <span className="flex items-center gap-2"><Pill status={po.status} /><strong className="tabular-nums">{formatCurrency(po.totalAmount, c)}</strong></span>
      </div>
      <div className="border border-[#D8DDE6] rounded overflow-x-auto">
        <table className="w-full text-[12px] sm:min-w-[38.75rem]">
          <thead className="text-[#706E6B] text-[11px] uppercase tracking-wide"><tr>
            <th className="text-left font-semibold px-3 py-2">Item</th><th className="text-right font-semibold px-3 py-2">Ordered</th>
            <th className="text-right font-semibold px-3 py-2">Billed</th><th className="text-right font-semibold px-3 py-2">Rate</th>
            {canBill && <th className="text-right font-semibold px-3 py-2 w-32">Bill now</th>}
          </tr></thead>
          <tbody>
            {po.lineItems.map((l) => (
              <tr key={l._id} className="border-t border-[#F3F2F2]">
                <td className="px-3 py-2">{l.description}</td>
                <td className="px-3 py-2 text-right tabular-nums">{l.quantity}</td>
                <td className="px-3 py-2 text-right tabular-nums">{l.billedQuantity}</td>
                <td className="px-3 py-2 text-right tabular-nums">{formatCurrency(l.unitPrice, c)} <span className="text-[#706E6B]">+{l.taxRate}%</span></td>
                {canBill && <td className="px-3 py-1.5"><input aria-label={`Quantity to bill for ${l.description}`} type="number" min="0" max={r2(l.quantity - l.billedQuantity)} step="0.01" className={cn(INPUT, 'text-right')} value={qty[l._id] || ''} onChange={(e) => setQty({ ...qty, [l._id]: e.target.value })} /></td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {canBill && (
        <div className="max-w-xs"><label className={LABEL} htmlFor="po-vbn">Vendor's invoice no. (optional)</label><input id="po-vbn" className={INPUT} value={vendorBillNumber} onChange={(e) => setVbn(e.target.value)} /></div>
      )}
      {canBill && <p className="text-[12px] text-[#706E6B]">Creates a draft bill for the quantities above (what was delivered). Approve the bill as usual to post it.</p>}
      {bills.length > 0 && (
        <div>
          <h3 className="text-[12px] font-bold text-[#706E6B] uppercase tracking-wide mb-2">Bills from this order</h3>
          <div className="space-y-1">
            {bills.map((b) => (
              <button type="button" key={b._id} onClick={() => onOpenBill?.(b._id)} className="w-full flex justify-between text-[12px] border border-[#C9C9C9] rounded px-3 py-2 hover:bg-[#F7F9FB]">
                <span className="font-semibold text-[#0176D3]">{b.billNumber}</span><span>{fmtDate(b.billDate)} · {b.status} · <strong className="tabular-nums">{formatCurrency(b.totalAmount, c)}</strong></span>
              </button>
            ))}
          </div>
        </div>
      )}
    </Modal>
  );
}

export default function PurchaseOrdersView({ vendors, accounts, onOpenBill, refreshKey, onChanged }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(null);
  const [detail, setDetail] = useState(null);
  const load = useCallback(() => {
    setLoading(true);
    api.get('/crm/purchases/purchase-orders').then((res) => setOrders(res.data.purchaseOrders || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);

  return (
    <div className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
      <div className="flex items-center justify-between p-3 border-b border-[#D8DDE6]">
        <p className="text-[13px] text-[#706E6B]">Orders you've placed. Nothing posts to your books until a bill raised from an order is approved.</p>
        <Btn primary onClick={() => setForm('new')} className="flex items-center gap-1.5"><Plus size={14} /> New order</Btn>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[13px] sm:min-w-[45rem]">
          <thead className="text-[#706E6B] text-[11px] uppercase tracking-wide"><tr className="border-b border-[#D8DDE6]">
            <th className="text-left font-semibold px-4 py-2.5">Order</th><th className="text-left font-semibold px-4 py-2.5">Vendor</th>
            <th className="text-left font-semibold px-4 py-2.5">Date</th><th className="text-left font-semibold px-4 py-2.5">Expected</th>
            <th className="text-right font-semibold px-4 py-2.5">Value</th><th className="text-left font-semibold px-4 py-2.5">Status</th>
          </tr></thead>
          <tbody>
            {orders.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-[#706E6B]">{loading ? 'Loading…' : 'No purchase orders yet.'}</td></tr>}
            {orders.map((o) => (
              <tr key={o._id} onClick={() => setDetail(o._id)} className="border-b border-[#F3F2F2] hover:bg-[#F7F9FB] cursor-pointer">
                <td className="px-4 py-2.5 font-semibold text-[#0176D3]">{o.poNumber}</td>
                <td className="px-4 py-2.5">{o.vendorId?.name || '—'}</td>
                <td className="px-4 py-2.5">{fmtDate(o.orderDate)}</td>
                <td className="px-4 py-2.5">{fmtDate(o.expectedDate)}</td>
                <td className="px-4 py-2.5 text-right tabular-nums">{formatCurrency(o.totalAmount, o.currency)}</td>
                <td className="px-4 py-2.5"><Pill status={o.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {form && <POForm po={form === 'new' ? null : form} vendors={vendors} accounts={accounts} onClose={() => setForm(null)} onSaved={() => { setForm(null); alertService.success('Order saved'); load(); }} />}
      {detail && !form && (
        <PODetail id={detail} onClose={() => setDetail(null)} onChanged={() => { load(); onChanged?.(); }}
          onEdit={(po) => setForm(po)} onOpenBill={(billId) => { setDetail(null); onOpenBill?.(billId); }} />
      )}
    </div>
  );
}
